import type {SenseAction} from 'src/action-creators/types';
import type {BatchAction} from 'src/action-creators/batch';

import * as Redux from 'redux';

import enableBatching, {type BatchReducer} from './batch';

import appConfig from './app-config';
import dataIntegration from './data-integration';
import accounts from './accounts';

import accountInvites from './account-invites';
import phoneNumbers from './phone-numbers';
import displayConfig from './display-config';
import agency from './agency';
import agencyOverview from './agency-overview';
import atsEntities from './ats-entities';
import jobVariables from './job-variables';
import languageSupport from './language-support';
import jobMatchMeta from './job-match-meta';
import journeyCategories from './journey-categories';

import audienceLists from './audience-lists';
import jobWidget from './job-widget';
import analyticsGenesis from './analytics-genesis';
import jobPreview from './job-preview';
import backgroundTask from './background-task';
import audienceMembers from './audience-members';
import workflows from './workflows';
import dynamicLabels from './dynamic-labels';
import events from './events';
import eventCreation from './event-creation';
import globalVariables from './global-variables';
import namespacedGlobalVariables from './namespaced-global-variables';
import requestCache from './request-cache';
import requestHeaders from './request-headers';
import roles from './roles';
import route from './route';
import groups from './groups';
import env from './env';
import groupAccounts from './group-accounts';
import groupAudience from './group-audience';
import report from './report';
import contentSubscription from './content-subscription';
import eventTypeCounts from './event-type-counts';
import sentEvents from './sent-events';
import dueEvents from './due-events';
import eventResponses from './event-responses';
import linkStats from './link-stats';
import plots from './plot';
import npsPlots from './nps-plot';
import csvImport, {type State as CsvImportState} from './csv-import';
import messages from './messages';
import threadLists from './thread-lists';
import modal from './modal';
import draftMessages from './draft-messages';
import broadcasts from './broadcasts';
import hvBroadcasts from './hv-broadcasts';
import contactsGenesis from './contacts-genesis';
import contacts from './contacts';
import contactLists from './contact-lists';
import messagesAnalytics from './messages-analytics';
import agentSettings from './agent-settings';
import flagging from './flagging';
import insightCounts from './insight-counts';
import atsSettings from './ats-settings';
import roi from './roi';
import engagementCohorts from './engagement-cohorts';
import templates from './templates';
import multientityAudience from './multientity-audience';
import emailDNS from './email-dns';
import {default as chat} from './chat';
import {default as scheduler} from './scheduler';
import dataExportReport from './data-export-report';
import productFlags from './product-flags';
import audit from './audit';
import writeback from './writeback';
import csrf from './csrf';
import navigation from './navigation';
import templateHelper from './template-helper';
import errors from './errors';
import chatbot from './chatbot';
import toasts from './toasts';
import workflowActivation from './workflow-activation';
import playbooks from './playbooks';
import audienceList from './audience-list';
import journeyAudience from './journey-audience';
import automationWorkflowAudience from './automation-workflow-audience';
import trigger from './trigger';
import entityLifecycleGoal from './entity-lifecycle-goal';
import referralV2 from './referral-v2';
import landingPage from './landing-page';
import referralAcl from './referral-acl';
import referralNotificationUsers from './referral-notification-user';
import referralExternal from './referral-external';
import fieldMapping from './field-mapping';
import abtAttributes from './action-based-targeting-meta';
import noteWritebackMetadata from './note-writeback-metadata';
import senseAIPanel from './sense-ai-panel';
import authentication from './authentication';
import referralProgram from './programs';
import referralSettings from 'src/reducers/referral-settings';
import journeyTouchpoint from './journey-touchpoint';
import referralProgramUsers from 'src/reducers/referral-program-user';
import senseAi from './sense-ai';
import drive from 'src/reducers/drive';
import referralFilters from 'src/reducers/referral-filter';
import delay from './delay';
import automationWorkflow from './automation-workflow';
import automationWorkflowOrganizer from './automation-workflow-organizer';
import targetEntityTypes from './target-entity-types';
import email from './email';
import sms from './sms';
import whatsapp from './whatsapp';
import fieldWriteback from './field-writeback';
import objectWriteback from './object-writeback';
import smsChatbot from './sms-chatbot';
import chatbotWhatsapp from './chatbot-whatsapp';
import voicebot from './voicebot';
import flowBuilder from './flow-builder';
import entityAttributes from './entity-attributes';
import pagesLeads from './leads';
import referrerPortal from './referrer-portal';

import pagesSites from './page-site';
import filter from './filter';
import surveySummary from './survey-summary';
import analyticsJourneysOverview from './analytics/analytics-journeys-overview';
import salesPerformanceAnalytics from './sales-performance-analytics';
import crm from './crm';
import analyticsWorkflowsOverview from './analytics/analytics-workflows-overview';
import analyticsSurveyAndNPS from './analytics/analytics-survey-and-nps';
import analyticsNPSDashboard from './analytics/analytics-nps-dashboard';
import analyticsRecruiterProductivity from './analytics/analytics-recruiter-productivity';
import analyticsHiringPipeline from './analytics/analytics-hiring-pipeline';
import analyticsAllApplicants from './analytics/analytics-all-applicants';
import analyticsVoiceCall from './analytics/analytics-voice-call';
import split from './split';
import customNoteWriteback from './custom-note-writeback';
import candidateMatching from './candidate-matching';
import aiGenerator from './ai-generator';
import candidateEvaluation from './candidate-evaluation';
import forEach from 'src/reducers/for-each';
import analyticsJourneysClickInsightsOverview from './analytics/analytics-journey-click-insights';
import analyticsCandidateSourcePerformance from './analytics/analytics-candidate-source-performance';
import analyticsReferrals from './analytics/analytics-referrals';
import audienceActions from './audience-actions';
import audienceCSV from './audience-csv-list';
import audienceATS from './audience-ats-list';
import crmMailbox from './crm-mailbox';
import calendarAuth from './calendar-auth';
import talentPools from './talent-pools';
import messagingSecureUpload from './messaging-secure-upload';
import discoverActiveJobs from './discover/active-jobs';

export type SenseReducer<S> = Redux.Reducer<S, SenseAction>;

export type State = ReturnType<typeof combinedReducer>;
export type GetState = () => State;

// TODO (kyle): fix this once SenseAction is fixed
type DispatchAction = (arg1: SenseAction) => any;
type DispatchThunk = <R>(arg1: ThunkAction<R>) => Promise<R>;
type DispatchThunkSync = (arg1: ThunkSyncAction) => void;
type DispatchBatch = (arg1: BatchAction) => void;

export type Dispatch = DispatchAction &
  DispatchThunk &
  DispatchThunkSync &
  DispatchBatch;
export type ThunkAction<R = undefined> = (
  arg1: Dispatch,
  arg2: GetState,
) => Promise<R>;
export type ThunkSyncAction = (arg1: Dispatch, arg2: GetState) => void;

// @ts-expect-error - TS2707 - Generic type 'Store<S, A>' requires between 0 and 2 type arguments.
export type ReduxStore = Redux.Store<State, SenseAction, Dispatch>;

const combinedReducer = Redux.combineReducers({
  //app
  appConfig,
  // API
  accounts,
  accountInvites,
  languageSupport,
  dataIntegration,
  phoneNumbers,
  displayConfig,
  agency,
  agencyOverview,
  atsEntities,
  jobVariables,
  jobMatchMeta,
  journeyCategories,
  audienceLists,
  jobWidget,
  analyticsGenesis,
  analyticsJourneysOverview,
  jobPreview,
  backgroundTask,
  audienceMembers,
  workflows,
  dynamicLabels,
  events,
  eventCreation,
  globalVariables,
  namespacedGlobalVariables,
  roles,
  groups,
  env,
  groupAccounts,
  groupAudience,
  report,
  contentSubscription,
  eventTypeCounts,
  sentEvents,
  dueEvents,
  linkStats,
  plots,
  npsPlots,
  eventResponses,
  csvImport,
  insightCounts,
  multientityAudience,
  chatbot,
  workflowActivation,
  referralV2,
  referralAcl,
  referralExternal,
  landingPage,
  fieldMapping,
  senseAIPanel,
  authentication,
  pagesLeads,
  pagesSites,

  // Framework
  requestHeaders, // important request headers from the server
  requestCache, // api cache with TTL
  route, // client routing and location
  modal, // modal service
  // Chat App
  messages,
  draftMessages,
  broadcasts,
  hvBroadcasts,
  contactsGenesis,
  threadLists,
  contacts,
  contactLists,
  messagesAnalytics,
  agentSettings,
  templates,
  chat,

  // scheduler
  scheduler,

  flagging,
  atsSettings,
  roi,
  engagementCohorts,
  emailDNS,

  // Data exports
  dataExportReport,

  productFlags,

  writeback,
  audit,
  csrf,
  navigation,
  templateHelper,
  errors,

  toasts,

  //playbooks
  playbooks,

  // Audience
  audienceATS,
  audienceCSV,
  audienceActions,

  //engage
  audienceList,
  journeyAudience,

  //automation workflow

  delay,
  automationWorkflow,
  automationWorkflowOrganizer,
  targetEntityTypes,
  email,
  sms,
  whatsapp,
  fieldWriteback,
  objectWriteback,
  smsChatbot,
  chatbotWhatsapp,
  entityAttributes,
  automationWorkflowAudience,
  trigger,
  filter,
  split,
  customNoteWriteback,
  candidateMatching,
  aiGenerator,
  forEach,
  candidateEvaluation,
  voicebot,

  // entity lifecycle goals
  entityLifecycleGoal,
  abtAttributes,
  // note writeback metadata
  noteWritebackMetadata,
  referralSettings,
  referralProgram,
  journeyTouchpoint,
  referralProgramUsers,
  // Sense AI
  senseAi,
  drive,
  referralNotificationUsers,
  referralFilters,
  referrerPortal,
  surveySummary,

  // Analytics
  salesPerformanceAnalytics,
  analyticsWorkflowsOverview,
  analyticsSurveyAndNPS,
  analyticsNPSDashboard,
  analyticsJourneysClickInsightsOverview,
  analyticsReferrals,
  analyticsRecruiterProductivity,
  analyticsCandidateSourcePerformance,

  analyticsHiringPipeline,
  analyticsAllApplicants,
  // CRM
  crm,
  // CRM Mailboxs
  crmMailbox,

  // Calendar Auth
  calendarAuth,

  // Talent Pools
  talentPools,

  // Messaging Secure Upload
  messagingSecureUpload,

  // Voiceflow builder
  flowBuilder,

  // Discover
  discoverActiveJobs,
  //voice call
  analyticsVoiceCall,
});

const batchedReducer: BatchReducer<State, SenseAction> =
  enableBatching(combinedReducer);
export default batchedReducer;
